import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Image,
  DropdownButton, Dropdown, InputGroup
} from 'react-bootstrap';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const ServicesManager = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Options
  const [availableIcons, setAvailableIcons] = useState({});
  
  const [formData, setFormData] = useState({
    service_name: '',
    slug: '',
    description: '',
    icon_class: '',
    image_url: '',
    is_active: true,
    service_order: 0
  });

  const API_BASE_URL = BASE_URL;
  
  useEffect(() => {
    // Fetch user data from localStorage or API
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    
    fetchServices();
    fetchOptions();
  }, []);

  const resetForm = () => {
    setFormData({
      service_name: '',
      slug: '',
      description: '',
      icon_class: '',
      image_url: '',
      is_active: true,
      service_order: 0
    });
    setImagePreview(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/admin/login';
  };

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/services.php?admin=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const processedServices = response.data.data.map(service => ({
          ...service,
          id: parseInt(service.id),
          service_order: parseInt(service.service_order),
          is_active: service.is_active === "1" || service.is_active === 1
        })).sort((a, b) => a.service_order - b.service_order);
        
        setServices(processedServices);
      } else {
        setError('Failed to load services');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      setOptionsLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/services.php?options=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setAvailableIcons(response.data.data.icons || {});
      }
    } catch (err) {
      console.error('Error fetching options:', err);
    } finally {
      setOptionsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Auto-generate slug from service name
    if (name === 'service_name' && !formData.slug && !editingService) {
      const slug = value.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: value,
        slug: slug
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSlugChange = (e) => {
    const slug = e.target.value
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-+|-+$/g, '');
    setFormData(prev => ({
      ...prev,
      slug: slug
    }));
  };

  const handleTextareaChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Set file for upload
      setFormData(prev => ({
        ...prev,
        image_file: file
      }));
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({
      ...prev,
      image_url: '',
      image_file: null
    }));
  };

  const uploadImage = async (file) => {
    try {
      setUploadingImage(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await axios.post(`${API_BASE_URL}/upload_service_image.php`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        return response.data.image_url;
      }
      return null;
    } catch (err) {
      console.error('Image upload error:', err);
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleIconSelect = (icon) => {
    setFormData(prev => ({
      ...prev,
      icon_class: icon
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let dataToSend = { ...formData };
      
      // Remove image_file from dataToSend if it exists
      if (dataToSend.image_file) {
        delete dataToSend.image_file;
      }
      
      // If there's a new image to upload
      if (formData.image_file) {
        const uploadedUrl = await uploadImage(formData.image_file);
        if (uploadedUrl) {
          dataToSend.image_url = uploadedUrl;
        } else {
          setError('Failed to upload image');
          return;
        }
      }
      
      // If order is not specified, set to next available
      if (!dataToSend.service_order || dataToSend.service_order <= 0) {
        const maxOrder = services.length > 0 
          ? Math.max(...services.map(service => parseInt(service.service_order))) 
          : 0;
        dataToSend.service_order = maxOrder + 1;
      }
      
      const token = localStorage.getItem('token');
      let response;
      
      if (editingService) {
        response = await axios.put(
          `${API_BASE_URL}/services.php?id=${editingService.id}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/services.php`, dataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      if (response.data.success) {
        setSuccess(editingService ? 'Service updated!' : 'Service added!');
        setShowModal(false);
        resetForm();
        setEditingService(null);
        fetchServices();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Operation failed: ' + (response.data.message || ''));
      }
    } catch (err) {
      setError('Error saving service: ' + (err.response?.data?.message || err.message));
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this service? This will also delete the associated image.')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API_BASE_URL}/services.php?id=${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          setSuccess('Service deleted!');
          fetchServices();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete service: ' + (response.data.message || ''));
        }
      } catch (err) {
        setError('Error deleting service');
        console.error(err);
      }
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      service_name: service.service_name,
      slug: service.slug,
      description: service.description || '',
      icon_class: service.icon_class || '',
      image_url: service.image_url || '',
      is_active: service.is_active === true || service.is_active === "1" || service.is_active === 1,
      service_order: parseInt(service.service_order)
    });
    
    // Set image preview if image_url exists
    if (service.image_url) {
      setImagePreview(service.image_url);
    }
    
    setShowModal(true);
  };

  const handleToggleActive = async (service) => {
    try {
      const currentActive = service.is_active === true || service.is_active === "1" || service.is_active === 1;
      const newActiveStatus = !currentActive;
      
      const updatedData = {
        service_name: service.service_name,
        slug: service.slug,
        description: service.description || '',
        icon_class: service.icon_class || '',
        image_url: service.image_url || '',
        is_active: newActiveStatus ? 1 : 0,
        service_order: parseInt(service.service_order)
      };
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/services.php?id=${service.id}`,
        updatedData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        fetchServices();
      } else {
        setError('Failed to update status: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Error toggling active status:', err);
      setError('Failed to update status');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingService(null);
    resetForm();
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    
    const items = Array.from(services);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update order numbers
    const updatedItems = items.map((item, index) => ({
      ...item,
      service_order: index + 1
    }));
    
    setServices(updatedItems);
    
    // Send update to server
    try {
      const token = localStorage.getItem('token');
      const reorderData = updatedItems.map(item => ({
        id: item.id,
        service_order: item.service_order
      }));
      
      const response = await axios.post(`${API_BASE_URL}/services.php`, {
        reorder: reorderData
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.data.success) {
        setError('Failed to save order changes');
        fetchServices(); // Revert to server state
      }
    } catch (err) {
      console.error('Error reordering:', err);
      setError('Failed to save order changes');
      fetchServices(); // Revert to server state
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'No description';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return `${UPLOAD_BASE_URL}${imagePath}`;
  };

  if (loading) {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading services...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Services Manager</h2>
            <p className="text-muted">Manage your services with drag & drop ordering</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => {
              const nextOrder = services.length > 0 
                ? Math.max(...services.map(service => parseInt(service.service_order))) + 1 
                : 1;
              setFormData(prev => ({ 
                ...prev, 
                service_order: nextOrder,
                is_active: true
              }));
              setShowModal(true);
            }}>
              <i className="fas fa-plus me-2"></i>Add Service
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <Card>
          <Card.Body>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="services">
                {(provided) => (
                  <Table responsive hover ref={provided.innerRef} {...provided.droppableProps}>
                    <thead>
                      <tr>
                        <th width="60">Drag</th>
                        <th width="80">Icon/Image</th>
                        <th>Service Name</th>
                        <th>Slug</th>
                        <th>Description</th>
                        <th>Order</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th width="150">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {services.map((service, index) => (
                        <Draggable key={service.id} draggableId={service.id.toString()} index={index}>
                          {(provided) => (
                            <tr 
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                            >
                              <td {...provided.dragHandleProps}>
                                <i className="fas fa-grip-vertical text-muted"></i>
                              </td>
                              <td>
                                <div className="d-flex align-items-center justify-content-center">
                                  {service.image_url ? (
                                    <Image 
                                      src={getImageUrl(service.image_url)} 
                                      alt={service.service_name}
                                      rounded
                                      style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                      className="border"
                                    />
                                  ) : service.icon_class ? (
                                    <div className="text-primary fs-4">
                                      <i className={service.icon_class}></i>
                                    </div>
                                  ) : (
                                    <div className="text-muted fs-4">
                                      <i className="fas fa-cube"></i>
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td>
                                <strong>{service.service_name}</strong>
                              </td>
                              <td>
                                <code className="small">{service.slug}</code>
                              </td>
                              <td>
                                <small className="text-muted">
                                  {truncateText(service.description, 60)}
                                </small>
                              </td>
                              <td>
                                <Badge bg="secondary">{service.service_order}</Badge>
                              </td>
                              <td>
                                <Form.Check
                                  type="switch"
                                  checked={service.is_active === true || service.is_active === "1" || service.is_active === 1}
                                  onChange={() => handleToggleActive(service)}
                                  label={(service.is_active === true || service.is_active === "1" || service.is_active === 1) ? 'Active' : 'Inactive'}
                                  key={`active-${service.id}-${service.is_active}`}
                                />
                              </td>
                              <td>
                                <small className="text-muted">
                                  {formatDate(service.created_at)}
                                </small>
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-2"
                                  onClick={() => handleEdit(service)}
                                >
                                  <i className="fas fa-edit"></i>
                                </Button>
                                
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDelete(service.id)}
                                >
                                  <i className="fas fa-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tbody>
                  </Table>
                )}
              </Droppable>
            </DragDropContext>
            
            {services.length === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-cogs fa-3x mb-3"></i>
                <p>No services found. Add your first service!</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Add/Edit Service Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingService ? 'Edit Service' : 'Add Service'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Service Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="service_name"
                      value={formData.service_name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Web Development"
                    />
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Order</Form.Label>
                    <Form.Control
                      type="number"
                      name="service_order"
                      value={formData.service_order}
                      onChange={handleInputChange}
                      min="1"
                    />
                    <Form.Text className="text-muted">
                      Lower numbers appear first
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Slug (URL)</Form.Label>
                <InputGroup>
                  <InputGroup.Text>/services/</InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleSlugChange}
                    required
                    placeholder="web-development"
                  />
                </InputGroup>
                <Form.Text className="text-muted">
                  URL-friendly identifier. Auto-generated from service name.
                </Form.Text>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleTextareaChange}
                  rows={4}
                  placeholder="Describe the service in detail..."
                />
              </Form.Group>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Icon</Form.Label>
                    <div className="d-flex align-items-center">
                      {formData.icon_class && (
                        <div className="text-primary fs-4 me-3">
                          <i className={formData.icon_class}></i>
                        </div>
                      )}
                      <DropdownButton
                        variant="outline-secondary"
                        title={formData.icon_class ? "Change Icon" : "Select Icon"}
                        onSelect={handleIconSelect}
                      >
                        {Object.entries(availableIcons).map(([icon, label]) => (
                          <Dropdown.Item key={icon} eventKey={icon}>
                            <i className={`${icon} me-2`}></i>
                            {label}
                          </Dropdown.Item>
                        ))}
                      </DropdownButton>
                      {formData.icon_class && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="ms-2"
                          onClick={() => setFormData(prev => ({ ...prev, icon_class: '' }))}
                        >
                          <i className="fas fa-times"></i>
                        </Button>
                      )}
                    </div>
                    <Form.Text className="text-muted">
                      Optional FontAwesome icon for the service
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <div>
                      <Form.Check
                        type="switch"
                        name="is_active"
                        label="Active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Inactive services won't be shown on the website
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Service Image</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      disabled={uploadingImage}
                    />
                    <Form.Text className="text-muted">
                      Upload an image for this service (optional, recommended: 800x600px)
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Image Preview */}
              {(imagePreview || formData.image_url) && (
                <div className="mb-3">
                  <Form.Label>Image Preview</Form.Label>
                  <div className="d-flex align-items-center">
                    <Image 
                      src={imagePreview?.startsWith('data:') ? imagePreview : getImageUrl(formData.image_url)}
                      alt="Preview" 
                      thumbnail 
                      style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover' }}
                      className="me-3"
                    />
                    
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={removeImage}
                    >
                      <i className="fas fa-trash me-1"></i> Remove Image
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Preview Section */}
              <Card className="border-secondary mt-3">
                <Card.Header>
                  <h6 className="mb-0">Preview</h6>
                </Card.Header>
                <Card.Body>
                  <div className="d-flex align-items-start">
                    <div className="me-3">
                      {formData.image_url || imagePreview ? (
                        <Image 
                          src={imagePreview?.startsWith('data:') ? imagePreview : getImageUrl(formData.image_url)}
                          alt="Service Preview" 
                          rounded
                          style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                          className="border"
                        />
                      ) : formData.icon_class ? (
                        <div className="text-primary fs-1">
                          <i className={formData.icon_class}></i>
                        </div>
                      ) : (
                        <div className="text-muted fs-1">
                          <i className="fas fa-cube"></i>
                        </div>
                      )}
                    </div>
                    <div>
                      <h5 className="mb-1">{formData.service_name || 'Service Name'}</h5>
                      <div className="text-muted mb-2">
                        <small>Slug: /services/{formData.slug || 'service-slug'}</small>
                      </div>
                      {formData.description && (
                        <p className="text-muted small mb-0">
                          {truncateText(formData.description, 120)}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-3 small text-muted">
                    <div className="d-flex">
                      <div className="me-4">
                        <i className="fas fa-sort-numeric-up me-1"></i>
                        Order: <span className="ms-2">{formData.service_order}</span>
                      </div>
                      <div className="me-4">
                        <i className="fas fa-circle me-1"></i>
                        Status: 
                        <span className={`ms-2 ${formData.is_active ? 'text-success' : 'text-warning'}`}>
                          {formData.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div>
                        <i className="fas fa-image me-1"></i>
                        {formData.image_url || imagePreview ? 'Custom Image' : formData.icon_class ? `Icon: ${formData.icon_class}` : 'No Media'}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
              
              {editingService && (
                <div className="alert alert-secondary mt-3">
                  <small>
                    <div className="row">
                      <div className="col-md-6">
                        <i className="fas fa-calendar-plus me-2"></i>
                        Created: {formatDate(editingService.created_at)}
                      </div>
                      <div className="col-md-6">
                        <i className="fas fa-sync-alt me-2"></i>
                        Last Updated: {formatDate(editingService.updated_at)}
                      </div>
                    </div>
                  </small>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploadingImage}>
                {uploadingImage && <Spinner animation="border" size="sm" className="me-2" />}
                {editingService ? 'Update' : 'Add'} Service
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default ServicesManager;